import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { verificationAPI } from '../lib/api';
import { Button } from './ui/button';
import { usePlan } from '../hooks/usePlan';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';

const REGIONS = {
  us: { label: 'United States', flag: '\ud83c\uddfa\ud83c\uddf8' },
  emea: { label: 'EMEA (Europe, Middle East & Africa)', flag: '\ud83c\uddea\ud83c\uddfa' },
  india: { label: 'India', flag: '\ud83c\uddee\ud83c\uddf3' },
};

const REGION_REQUIREMENTS = {
  us: [
    { document_type: 'Social Security Number (SSN)', description: 'Provide your 9-digit SSN for identity and employment verification', is_mandatory: true, has_expiry: false, category: 'identity', requires_id_number: true, id_label: 'SSN (XXX-XX-XXXX)', id_mask: '***-**-****' },
    { document_type: 'US Driver\'s License', description: 'Valid state-issued driver\'s license with photo', is_mandatory: true, has_expiry: true, category: 'identity', requires_id_number: true, id_label: 'License Number' },
    { document_type: 'US Passport', description: 'Valid US passport (if available)', is_mandatory: false, has_expiry: true, category: 'identity', requires_id_number: true, id_label: 'Passport Number' },
    { document_type: 'I-9 Employment Eligibility', description: 'Completed Form I-9 for employment authorization', is_mandatory: true, has_expiry: false, category: 'work_authorization' },
    { document_type: 'Education Verification', description: 'Degree certificate, diploma, or official transcript', is_mandatory: true, has_expiry: false, category: 'education' },
    { document_type: 'Professional License', description: 'State or federal professional license (if applicable)', is_mandatory: false, has_expiry: true, category: 'certification', requires_id_number: true, id_label: 'License Number' },
    { document_type: 'Background Check Consent (FCRA)', description: 'Signed Fair Credit Reporting Act consent form', is_mandatory: true, has_expiry: false, category: 'background_check' },
    { document_type: 'W-4 Tax Withholding', description: 'Completed IRS Form W-4', is_mandatory: false, has_expiry: false, category: 'other' },
    { document_type: 'Drug Screening Consent', description: 'Signed consent for pre-employment drug test', is_mandatory: false, has_expiry: false, category: 'medical' },
  ],
  emea: [
    { document_type: 'National Identity Card', description: 'Government-issued national ID card with photo', is_mandatory: true, has_expiry: true, category: 'identity', requires_id_number: true, id_label: 'National ID Number' },
    { document_type: 'Passport', description: 'Valid passport from country of citizenship', is_mandatory: true, has_expiry: true, category: 'identity', requires_id_number: true, id_label: 'Passport Number' },
    { document_type: 'Residence Permit', description: 'Valid residence/work permit (for non-EU nationals)', is_mandatory: false, has_expiry: true, category: 'work_authorization', requires_id_number: true, id_label: 'Permit Number' },
    { document_type: 'Tax Identification Number (TIN)', description: 'Tax ID / VAT number issued by local tax authority', is_mandatory: true, has_expiry: false, category: 'identity', requires_id_number: true, id_label: 'Tax ID / TIN' },
    { document_type: 'Proof of Address', description: 'Utility bill, bank statement, or government letter (within 3 months)', is_mandatory: true, has_expiry: false, category: 'identity' },
    { document_type: 'Education Certificate', description: 'Degree certificate or official academic transcript', is_mandatory: true, has_expiry: false, category: 'education' },
    { document_type: 'Professional Certification', description: 'Industry or professional body certification', is_mandatory: false, has_expiry: true, category: 'certification', requires_id_number: true, id_label: 'Certificate Number' },
    { document_type: 'GDPR Data Processing Consent', description: 'Signed consent for processing personal data under GDPR', is_mandatory: true, has_expiry: false, category: 'background_check' },
    { document_type: 'Criminal Record Check', description: 'Police clearance certificate / DBS check / Casier Judiciaire', is_mandatory: false, has_expiry: true, category: 'background_check' },
    { document_type: 'Right to Work Document', description: 'Proof of right to work in the country of employment', is_mandatory: true, has_expiry: true, category: 'work_authorization' },
  ],
  india: [
    { document_type: 'Aadhaar Card', description: '12-digit Aadhaar number issued by UIDAI for identity verification', is_mandatory: true, has_expiry: false, category: 'identity', requires_id_number: true, id_label: 'Aadhaar Number (XXXX XXXX XXXX)' },
    { document_type: 'PAN Card', description: '10-character Permanent Account Number for tax and financial verification', is_mandatory: true, has_expiry: false, category: 'identity', requires_id_number: true, id_label: 'PAN (ABCDE1234F)' },
    { document_type: 'Indian Passport', description: 'Valid Indian passport', is_mandatory: false, has_expiry: true, category: 'identity', requires_id_number: true, id_label: 'Passport Number' },
    { document_type: 'Voter ID (EPIC)', description: 'Election Photo Identity Card', is_mandatory: false, has_expiry: false, category: 'identity', requires_id_number: true, id_label: 'EPIC Number' },
    { document_type: 'Driving License', description: 'Valid Indian driving license', is_mandatory: false, has_expiry: true, category: 'identity', requires_id_number: true, id_label: 'DL Number' },
    { document_type: 'Education Certificates', description: 'Degree certificate, marksheet, or UGC-recognized qualification', is_mandatory: true, has_expiry: false, category: 'education' },
    { document_type: 'Professional Certification', description: 'Industry certification (e.g., CA, CS, ICAI, etc.)', is_mandatory: false, has_expiry: true, category: 'certification', requires_id_number: true, id_label: 'Membership/Cert Number' },
    { document_type: 'Previous Employment Verification', description: 'Experience letter, relieving letter, or offer letter from previous employer', is_mandatory: true, has_expiry: false, category: 'other' },
    { document_type: 'Address Proof', description: 'Aadhaar, utility bill, or bank statement showing current address', is_mandatory: true, has_expiry: false, category: 'identity' },
    { document_type: 'Police Verification Certificate', description: 'Police clearance from local police station', is_mandatory: false, has_expiry: true, category: 'background_check' },
    { document_type: 'UAN / PF Details', description: 'Universal Account Number for Provident Fund verification', is_mandatory: false, has_expiry: false, category: 'other', requires_id_number: true, id_label: 'UAN Number' },
  ],
};

const CATEGORY_LABELS = {
  identity: 'Identity Verification', education: 'Education', certification: 'Certifications',
  background_check: 'Background Checks', medical: 'Medical', work_authorization: 'Work Authorization', other: 'Other',
};

// Sub-components
function CandidateInfoSection({ candidate, candidateEmail, setCandidateEmail, inputStyle }) {
  return (
    <div style={{ padding: '0.875rem', borderRadius: 10, background: '#F1EAE1', border: '1px solid #ECE7DE', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #0076A3, #00A0E0)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8125rem', fontWeight: 600, color: '#fff' }}>
          {candidate?.name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#002A38' }}>{candidate?.name || 'Unknown'}</div>
          <input value={candidateEmail} onChange={e => setCandidateEmail(e.target.value)} placeholder="candidate@email.com"
            style={{ ...inputStyle, marginTop: '0.375rem', padding: '0.375rem 0.625rem', fontSize: '0.75rem' }} />
        </div>
      </div>
    </div>
  );
}

function RequirementItem({ req, isSelected, onToggle }) {
  return (
    <button onClick={onToggle} style={{
      display: 'flex', alignItems: 'flex-start', gap: '0.625rem', padding: '0.625rem 0.75rem', borderRadius: 8, cursor: 'pointer',
      border: `1px solid ${isSelected ? '#0076A3' : '#ECE7DE'}`, background: isSelected ? 'rgba(30,104,255,0.04)' : '#FAFBFC',
      textAlign: 'left', width: '100%', transition: 'border-color 0.15s',
    }}>
      <div style={{
        width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
        border: `2px solid ${isSelected ? '#0076A3' : '#CBD5E1'}`, background: isSelected ? '#0076A3' : '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {isSelected && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#002A38' }}>
          {req.document_type}
          {req.is_mandatory && <span style={{ color: '#DC2626', marginLeft: 4, fontSize: '0.625rem', fontWeight: 400 }}>Required</span>}
          {req.has_expiry && <span style={{ color: '#D97706', marginLeft: 4, fontSize: '0.625rem', fontWeight: 400 }}>Has Expiry</span>}
          {req.requires_id_number && <span style={{ color: '#0076A3', marginLeft: 4, fontSize: '0.625rem', fontWeight: 400 }}>ID Required</span>}
        </div>
        {req.description && <div style={{ fontSize: '0.6875rem', color: '#94A3B8', marginTop: '0.125rem' }}>{req.description}</div>}
      </div>
    </button>
  );
}

// Main Dialog
export default function VerificationSendDialog({ open, onClose, candidate, jobId }) {
  const navigate = useNavigate();
  const { isStarter } = usePlan();
  const [region, setRegion] = useState('us');
  const [requirements, setRequirements] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [candidateEmail, setCandidateEmail] = useState('');
  const [customDoc, setCustomDoc] = useState('');
  const [bgv, setBgv] = useState(null);

  useEffect(() => {
    if (open && jobId) {
      loadRequirements();
      setCandidateEmail(candidate?.parsed_resume?.email || candidate?.email || '');
      verificationAPI.getBgvUsage().then(r => setBgv(r.data)).catch(() => setBgv(null));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  useEffect(() => {
    // When region changes, load region-specific requirements
    const reqs = REGION_REQUIREMENTS[region] || REGION_REQUIREMENTS.us;
    setRequirements(reqs);
    setSelected(new Set(reqs.filter(r => r.is_mandatory).map(r => r.document_type)));
  }, [region]);

  const loadRequirements = async () => {
    setLoading(true);
    try {
      const res = await verificationAPI.getRequirements(jobId);
      if (res.data.requirements?.length > 0) {
        setRequirements(res.data.requirements);
        setSelected(new Set(res.data.requirements.filter(r => r.is_mandatory).map(r => r.document_type)));
      }
    } catch (err) {
      // Use region defaults
    } finally { setLoading(false); }
  };

  const handleGenerateAI = async () => {
    setGenerating(true);
    try {
      const res = await verificationAPI.generateRequirements(jobId);
      const reqs = res.data.requirements || [];
      if (reqs.length > 0) {
        // Merge AI reqs with region-specific ID requirements
        const regionReqs = REGION_REQUIREMENTS[region] || [];
        const idReqs = regionReqs.filter(r => r.requires_id_number);
        const merged = [...idReqs, ...reqs.filter(r => !idReqs.some(ir => ir.document_type === r.document_type))];
        setRequirements(merged);
        setSelected(new Set(merged.filter(r => r.is_mandatory).map(r => r.document_type)));
        toast.success(`Generated ${merged.length} requirements (${REGIONS[region].label})`);
      }
    } catch (err) {
      toast.error('Failed to generate. Using region defaults.');
    } finally { setGenerating(false); }
  };

  const toggleRequirement = (docType) => {
    setSelected(prev => { const next = new Set(prev); next.has(docType) ? next.delete(docType) : next.add(docType); return next; });
  };

  const selectAll = () => setSelected(new Set(requirements.map(r => r.document_type)));
  const selectNone = () => setSelected(new Set());
  const selectMandatory = () => setSelected(new Set(requirements.filter(r => r.is_mandatory).map(r => r.document_type)));

  const addCustomRequirement = () => {
    if (!customDoc.trim()) return;
    const newReq = { document_type: customDoc.trim(), description: '', is_mandatory: false, has_expiry: false, category: 'other' };
    setRequirements(prev => [...prev, newReq]);
    setSelected(prev => new Set([...prev, newReq.document_type]));
    setCustomDoc('');
  };

  const handleSend = async () => {
    if (selected.size === 0) { toast.error('Please select at least one document'); return; }
    if (!candidateEmail.trim()) { toast.error('Please enter candidate email'); return; }
    setSending(true);
    try {
      const selectedReqs = requirements.filter(r => selected.has(r.document_type));
      await verificationAPI.updateRequirements(jobId, { requirements: selectedReqs });
      const res = await verificationAPI.createRequest({ job_id: jobId, candidate_name: candidate?.name || 'Unknown', candidate_email: candidateEmail, region });
      const link = `${window.location.origin}/verify/${res.data.access_code}`;
      navigator.clipboard.writeText(link);
      toast.success(`Verification request sent! Link copied to clipboard.`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification request');
    } finally { setSending(false); }
  };

  const inputStyle = { width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #CBD5E1', background: '#FAF7F2', color: '#002A38', fontSize: '0.8125rem', outline: 'none', boxSizing: 'border-box' };

  const grouped = {};
  requirements.forEach(r => { const cat = r.category || 'other'; if (!grouped[cat]) grouped[cat] = []; grouped[cat].push(r); });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, maxWidth: 600, maxHeight: '85vh', overflow: 'auto' }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.125rem' }}>Send Verification Request</DialogTitle>
        </DialogHeader>

        <CandidateInfoSection candidate={candidate} candidateEmail={candidateEmail} setCandidateEmail={setCandidateEmail} inputStyle={inputStyle} />

        {/* Region Selector */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748B', marginBottom: '0.5rem' }}>Verification Region</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {Object.entries(REGIONS).map(([key, val]) => (
              <button key={key} onClick={() => setRegion(key)} style={{
                flex: 1, padding: '0.625rem 0.5rem', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                border: `2px solid ${region === key ? '#0076A3' : '#ECE7DE'}`,
                background: region === key ? 'rgba(43,108,176,0.06)' : '#fff',
                transition: 'border-color 0.15s',
              }}>
                <div style={{ fontSize: '1.25rem', marginBottom: '0.25rem' }}>{val.flag}</div>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: region === key ? '#0076A3' : '#64748B' }}>{val.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Requirements */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#002A38' }}>
              Documents <span style={{ fontWeight: 400, color: '#64748B', marginLeft: 4 }}>({selected.size} selected)</span>
            </h4>
            <Button variant="outline" onClick={handleGenerateAI} disabled={generating} style={{ borderRadius: 8, fontSize: '0.6875rem', padding: '0.25rem 0.625rem' }}>
              {generating ? 'Generating...' : 'Generate from JD'}
            </Button>
          </div>

          <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '0.625rem' }}>
            {[
              { fn: selectAll, label: 'All', color: '#0076A3' },
              { fn: selectMandatory, label: 'Mandatory', color: '#D97706' },
              { fn: selectNone, label: 'Clear', color: '#64748B' },
            ].map(b => (
              <button key={b.label} onClick={b.fn} style={{ padding: '0.1875rem 0.5rem', borderRadius: 6, border: '1px solid #ECE7DE', background: '#FAF7F2', cursor: 'pointer', fontSize: '0.625rem', color: b.color }}>
                {b.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748B' }}>Loading...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', maxHeight: 300, overflow: 'auto' }}>
              {Object.entries(grouped).map(([category, reqs]) => (
                <div key={category}>
                  <div style={{ fontSize: '0.625rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                    {CATEGORY_LABELS[category] || category}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {reqs.map(req => (
                      <RequirementItem key={req.document_type} req={req} isSelected={selected.has(req.document_type)} onToggle={() => toggleRequirement(req.document_type)} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.625rem' }}>
            <input value={customDoc} onChange={e => setCustomDoc(e.target.value)} placeholder="Add custom document..." onKeyDown={e => { if (e.key === 'Enter') addCustomRequirement(); }} style={{ ...inputStyle, flex: 1, fontSize: '0.75rem' }} />
            <Button variant="outline" onClick={addCustomRequirement} disabled={!customDoc.trim()} style={{ borderRadius: 8, fontSize: '0.75rem', padding: '0.375rem 0.625rem', whiteSpace: 'nowrap' }}>+ Add</Button>
          </div>
        </div>

        <div style={{ padding: '0.625rem', borderRadius: 8, background: 'rgba(30,104,255,0.06)', border: '1px solid rgba(30,104,255,0.12)', fontSize: '0.6875rem', color: '#0076A3', marginBottom: '0.75rem' }}>
          Region: <strong>{REGIONS[region].flag} {REGIONS[region].label}</strong> — documents with <span style={{ color: '#0076A3' }}>ID Required</span> will ask the candidate to enter their ID number during upload.
        </div>

        {isStarter && (
          <div data-testid="bgv-upgrade-note" style={{
            padding: '0.75rem 0.9rem', borderRadius: 10, marginBottom: '0.75rem', fontSize: '0.75rem',
            background: 'linear-gradient(90deg,#F5F3FF,#EEF2FF)', border: '1px solid #DDD6FE', color: '#5B21B6',
          }}>
            <strong>Background Verification is a Growth feature.</strong> Your Starter plan doesn’t include BGV. Upgrade to Growth (50 checks/mo) in Billing to send verification requests.
          </div>
        )}

        {!isStarter && bgv && (
          <div data-testid="bgv-check-note" style={{
            padding: '0.625rem 0.75rem', borderRadius: 8, marginBottom: '0.75rem', fontSize: '0.6875rem',
            background: selected.size > bgv.total_available ? '#FEF2F2' : '#F0FDF4',
            border: `1px solid ${selected.size > bgv.total_available ? '#FECACA' : '#BBF7D0'}`,
            color: selected.size > bgv.total_available ? '#B91C1C' : '#166534',
          }}>
            This request uses <strong>{selected.size} BGV check{selected.size !== 1 ? 's' : ''}</strong> (1 per document). You have <strong>{bgv.total_available}</strong> available
            {bgv.monthly_allotment > 0 ? ` (${bgv.plan_checks_remaining} free this month + ${bgv.extra_checks} prepaid)` : ''}.
            {selected.size > bgv.total_available && ' Not enough checks — buy more in Billing → Buy BGV Checks.'}
          </div>
        )}

        <DialogFooter style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.75rem', color: '#64748B' }}>{selected.size} doc{selected.size !== 1 ? 's' : ''}</span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button variant="outline" onClick={onClose} style={{ borderRadius: 10 }}>Cancel</Button>
            {isStarter ? (
              <Button data-testid="bgv-upgrade-btn" onClick={() => { onClose(); navigate('/payment?plan=professional_monthly'); }} style={{ background: '#F2600C', color: '#fff', borderRadius: 999, fontWeight: 600 }}>
                Upgrade to Growth
              </Button>
            ) : (
              <Button data-testid="send-verification-btn" onClick={handleSend} disabled={sending || selected.size === 0 || (bgv && selected.size > bgv.total_available)} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }}>
                {sending ? 'Sending...' : 'Send Verification Request'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
